import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { AttendanceCorrectionDetailFields } from '../../components/AttendanceCorrectionDetailFields';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { LeaveStatusBadge } from '../../components/LeaveStatusBadge';
import { PermissionGuard } from '../../components/PermissionGuard';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { getAttendanceCorrectionModuleCapabilities } from '../../config/attendanceCorrectionCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import { type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  cancelAttendanceCorrectionRequest,
  getAttendanceCorrectionRequestDetails,
} from '../../services/attendanceCorrectionService';
import type { AttendanceCorrectionRequestDetails } from '../../types/attendanceCorrection';
import { attendanceCorrectionErrorMessage } from '../../utils/attendanceCorrectionErrors';
import { attendanceCorrectionDisplayRecord } from '../../utils/attendanceCorrectionSubmittedFields';
import { isRejectedWorkflowStatus, isCancelledWorkflowStatus } from '../../utils/workflowStatus';

type Props = NativeStackScreenProps<
  RootStackParamList,
  'AttendanceCorrectionDetails'
>;

export function AttendanceCorrectionRequestDetailsScreen({
  navigation,
  route,
}: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getAttendanceCorrectionModuleCapabilities(
    auth.permissions,
  );
  const canAccess = auth.hasPermission(PERMISSIONS.ATTENDANCE_VIEW);
  const correctionRequestId = route.params.correctionRequestId;

  const [details, setDetails] =
    useState<AttendanceCorrectionRequestDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCancelReason, setShowCancelReason] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!canAccess) {
      setLoading(false);
      return;
    }
    if (!Number.isInteger(correctionRequestId) || correctionRequestId <= 0) {
      setDetails(null);
      setError('Attendance correction request was not found.');
      setLoading(false);
      return;
    }
    if (!capabilities.canViewDetails) {
      setDetails(null);
      setError('Attendance correction details are not currently available.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result =
        await getAttendanceCorrectionRequestDetails(correctionRequestId);
      setDetails(result);
    } catch (caught) {
      setDetails(null);
      setError(
        attendanceCorrectionErrorMessage(
          caught,
          'Unable to load attendance correction request details.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [canAccess, capabilities.canViewDetails, correctionRequestId]);

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  const themed = useMemo(
    () => ({
      actionError: {
        ...typography.caption,
        color: palette.error,
        marginBottom: spacing.md,
      },
    }),
    [palette],
  );

  const confirmCancel = async () => {
    const trimmedReason = cancelReason.trim();
    if (!trimmedReason) {
      setActionError('Cancellation reason is required.');
      return;
    }
    setCancelling(true);
    setActionError(null);
    try {
      await cancelAttendanceCorrectionRequest(
        correctionRequestId,
        trimmedReason,
      );
      navigation.goBack();
    } catch (caught) {
      setActionError(
        attendanceCorrectionErrorMessage(
          caught,
          'Unable to cancel attendance correction request.',
        ),
      );
    } finally {
      setCancelling(false);
    }
  };

  return (
    <PermissionGuard
      permission={PERMISSIONS.ATTENDANCE_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Correction Details"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading correction details" />
        </AppScreen>
      ) : error && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Correction Details"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={load} />
        </AppScreen>
      ) : details ? (
        <AppScreen scroll>
          <ScreenHeader
            title="Correction Details"
            onBack={navigation.goBack}
          />

          <View style={styles.body}>
            <AppCard style={styles.card}>
              <View style={styles.statusRow}>
                <LeaveStatusBadge
                  label={details.statusLabel}
                  statusCode={details.statusCode}
                />
              </View>
              <AttendanceCorrectionDetailFields
                requestDetails={attendanceCorrectionDisplayRecord(details)}
                submittedAt={details.submittedAt}
                rejectReason={details.rejectReason}
                cancellationReason={details.cancellationReason}
                showRejectReason={isRejectedWorkflowStatus(details.statusCode)}
                showCancellationReason={isCancelledWorkflowStatus(
                  details.statusCode,
                )}
                fallbackEditorName={details.requesterName}
                palette={palette}
              />
            </AppCard>

            {capabilities.canCancel && details.canCancel ? (
              showCancelReason ? (
                <View style={styles.cancelBlock}>
                  <AppInput
                    label="Cancellation reason"
                    value={cancelReason}
                    onChangeText={value => {
                      setCancelReason(value);
                      setActionError(null);
                    }}
                    placeholder="Enter reason for cancellation"
                  />
                  {actionError ? (
                    <Text accessibilityRole="alert" style={themed.actionError}>
                      {actionError}
                    </Text>
                  ) : null}
                  <AppButton
                    title="Confirm Cancel"
                    variant="danger"
                    onPress={confirmCancel}
                    loading={cancelling}
                    disabled={cancelling}
                  />
                  <AppButton
                    title="Keep Request"
                    variant="text"
                    onPress={() => {
                      setShowCancelReason(false);
                      setCancelReason('');
                      setActionError(null);
                    }}
                    disabled={cancelling}
                  />
                </View>
              ) : (
                <AppButton
                  title="Cancel Request"
                  variant="danger"
                  onPress={() => setShowCancelReason(true)}
                />
              )
            ) : null}
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center', gap: spacing.md },
  card: { gap: 0, marginBottom: spacing.md, paddingBottom: spacing.sm },
  statusRow: {
    alignItems: 'flex-end',
    marginBottom: spacing.md,
  },
  cancelBlock: { gap: spacing.md, marginBottom: spacing.xl },
});
